import { useCallback, useEffect, useState } from 'react';
import {
  isPushSupported,
  requestNotificationPermission,
} from '../lib/firebase-messaging';
import { registerPushOnce } from '../lib/push-register';

export type PushStatus = 'unsupported' | 'prompt' | 'granted' | 'denied' | 'registered' | 'error';

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [status, setStatus] = useState<PushStatus>('prompt');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }
    setPermission(Notification.permission);
    if (Notification.permission === 'denied') setStatus('denied');
    if (Notification.permission === 'granted') setStatus('granted');
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isPushSupported()) return false;
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'denied') {
      setStatus('denied');
      return false;
    }
    if (result === 'granted') setStatus('granted');
    return result === 'granted';
  }, []);

  const register = useCallback(async (): Promise<boolean> => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return false;
    }

    try {
      const ok = await registerPushOnce();
      if (!ok) {
        const permissionNow =
          typeof Notification !== 'undefined' ? Notification.permission : 'denied';
        setPermission(permissionNow);
        if (permissionNow === 'denied') {
          setStatus('denied');
        } else if (permissionNow === 'granted') {
          // Permissão ok; o token pode ainda estar a gravar no servidor.
          setStatus('granted');
        } else {
          setStatus('error');
        }
        return false;
      }
      setPermission('granted');
      setStatus('registered');
      return true;
    } catch (error) {
      console.warn('Push: falha ao registrar', error);
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        setStatus('granted');
      } else {
        setStatus('error');
      }
      return false;
    }
  }, []);

  return {
    isSupported: isPushSupported(),
    permission,
    status,
    requestPermission,
    register,
  };
}
